import { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useCompanyAuth } from '../../context/CompanyAuthContext';

const NAV = [
  { to: '/company/dashboard',    icon: 'ti-layout-dashboard', label: 'Dashboard'    },
  { to: '/company/assessments',  icon: 'ti-file-description', label: 'Assessments'  },
  { to: '/company/team',         icon: 'ti-users',            label: 'Team'         },
  { to: '/company/credits',      icon: 'ti-coin',             label: 'Credits'      },
  { to: '/company/profile',      icon: 'ti-building',         label: 'Profile'      },
];

const ROLE_BADGE = { owner: '#1B3A5C', hr: '#059669', approver: '#D97706' };

export default function CompanyLayout({ children }) {
  const { companyUser, logout } = useCompanyAuth();
  const location  = useLocation();
  const navigate  = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = () => { logout(); navigate('/company/login'); };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', fontFamily: "'Inter',-apple-system,sans-serif" }}>
      {/* Sidebar */}
      <aside style={{ width: 240, background: '#0F172A', display: 'flex', flexDirection: 'column', flexShrink: 0, position: 'sticky', top: 0, height: '100vh', overflowY: 'auto' }}>
        <div style={{ padding: '24px 20px 16px' }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#fff', letterSpacing: '-0.02em', marginBottom: 4 }}>
            Centric<span style={{ color: '#C1272D' }}>Q</span>
          </div>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Company Portal</div>
        </div>

        {/* Company info */}
        <div style={{ margin: '0 12px 16px', background: 'rgba(255,255,255,0.05)', borderRadius: 10, padding: '12px 14px' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#fff', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {companyUser?.company_name}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 10, color: '#fff', fontWeight: 600, background: ROLE_BADGE[companyUser?.role] || '#1B3A5C', padding: '2px 7px', borderRadius: 4 }}>
              {companyUser?.role}
            </span>
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {companyUser?.full_name}
            </span>
          </div>
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: '0 12px' }}>
          {NAV.map(({ to, icon, label }) => {
            const active = location.pathname.startsWith(to);
            return (
              <Link key={to} to={to} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px',
                borderRadius: 8, marginBottom: 2, textDecoration: 'none',
                background: active ? 'rgba(255,255,255,0.08)' : 'transparent',
                color: active ? '#fff' : 'rgba(255,255,255,0.5)',
                fontSize: 13, fontWeight: active ? 600 : 400,
                transition: 'all 0.15s',
              }}>
                <i className={icon} style={{ fontSize: 16 }} />
                {label}
              </Link>
            );
          })}
        </nav>

        {/* Logout */}
        <div style={{ padding: '12px 12px 24px' }}>
          <button onClick={handleLogout} style={{
            width: '100%', padding: '9px 12px', background: 'transparent',
            border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8,
            color: 'rgba(255,255,255,0.4)', fontSize: 13, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <i className="ti-logout" style={{ fontSize: 15 }} />
            Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main style={{ flex: 1, background: '#F0F6FF', minHeight: '100vh', overflow: 'auto' }}>
        {children}
      </main>
    </div>
  );
}

// Guard — redirect to login if not authenticated; redirect to activate if not activated
export function CompanyProtectedRoute({ children }) {
  const { companyUser, loading } = useCompanyAuth();

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 32, height: 32, border: '3px solid #1B3A5C', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
    </div>
  );

  if (!companyUser) {
    window.location.href = '/company/login';
    return null;
  }

  if (!companyUser.company_is_activated) {
    return <Navigate to="/company/activate" replace />;
  }

  return children;
}
